import { useEffect, useState, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client';
import './Layout.css';

const ICON_PROPS = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

const FeedIcon = () => (
  <svg {...ICON_PROPS}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
);
const BidsIcon = () => (
  <svg {...ICON_PROPS}><path d="M14 3l7 7-4 4-7-7 4-4z" /><path d="M9 8l-6 6 4 4 6-6" /><line x1="14" y1="14" x2="20" y2="20" /></svg>
);
const WishlistIcon = () => (
  <svg {...ICON_PROPS}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
);
const PricesIcon = () => (
  <svg {...ICON_PROPS}><polyline points="22 12 18 8 14 12 10 4 6 10 2 8" /><line x1="2" y1="20" x2="22" y2="20" /></svg>
);
const NotifIcon = () => (
  <svg {...ICON_PROPS}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
);
const ProfileIcon = () => (
  <svg {...ICON_PROPS}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);

export default function Layout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bottomNavVisible, setBottomNavVisible] = useState(true);
  const [feePopupOpen, setFeePopupOpen] = useState(false);
  const lastScrollY = useRef(0);
  const idleTimer = useRef(null);

  const { data: unreadData } = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => api.get('/notifications/unread-count').then(r => r.data),
    refetchInterval: 60000,
  });

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;

      if (y < 10) {
        setBottomNavVisible(true);
      } else if (delta > 5) {
        setBottomNavVisible(false);
      } else if (delta < -5) {
        setBottomNavVisible(true);
      }
      lastScrollY.current = y;

      // Reappear when scroll stops
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setBottomNavVisible(true), 200);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, []);

  return (
    <div className="layout">
      <header className="header">
        <div className="header-inner page">
          <button className="brand" onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/logo.png" alt="The Daily Bid" className="brand-logo" />
          </button>
          <nav className="nav">
            <div className="nav-left">
              <NavLink to="/" end>Products</NavLink>
              <NavLink to="/wishlist">Wishlist</NavLink>
              {user?.user_type !== 'professional' && <NavLink to="/market-prices">Market Prices</NavLink>}
              <NavLink to="/bid-centre">Bid Centre</NavLink>
              {user?.is_admin && <NavLink to="/admin" className="nav-admin">Admin</NavLink>}
            </div>
            <div className="nav-right">
              <NavLink to="/notifications" className="nav-icon-link">
                <NotifIcon />
                {unreadData?.count > 0 && <span className="notif-dot">{unreadData.count}</span>}
              </NavLink>
              <NavLink to="/profile" className="nav-icon-link">
                <ProfileIcon />
              </NavLink>
            </div>
          </nav>
        </div>
      </header>
      <main className="main">
        <div className="page">
          <Outlet />
        </div>
      </main>

      <footer className="site-footer">
        <div className="page">
          <p className="footer-text">
            The Daily Bid acts as an independent auction agent. We are not affiliated with, endorsed by, or associated with any of the brands or auction houses whose products appear on this platform.
            Our service is subject to a fee included in the final price.{' '}
            <button className="footer-link" onClick={() => setFeePopupOpen(true)}>View our fee schedule</button>.
          </p>
        </div>
      </footer>

      {feePopupOpen && (
        <div className="fee-overlay" onClick={() => setFeePopupOpen(false)}>
          <div className="fee-popup" onClick={e => e.stopPropagation()}>
            <button className="fee-close" onClick={() => setFeePopupOpen(false)}>&times;</button>
            <h3>Our fee schedule</h3>
            <p className="fee-intro">Our commission is included in the price you see. It covers sourcing, bidding, authentication, shipping, customs and delivery.</p>
            {user?.user_type === 'professional' ? (
              <table className="fee-table">
                <thead>
                  <tr><th>Price range</th><th>Commission</th></tr>
                </thead>
                <tbody>
                  <tr><td>Under €500</td><td>20%</td></tr>
                  <tr><td>€500 — €1,000</td><td>17%</td></tr>
                  <tr><td>€1,000 — €3,000</td><td>15%</td></tr>
                  <tr><td>Over €3,000</td><td>13%</td></tr>
                </tbody>
              </table>
            ) : (
              <table className="fee-table">
                <thead>
                  <tr><th>Price range</th><th>Commission</th></tr>
                </thead>
                <tbody>
                  <tr><td>Under €500</td><td>28%</td></tr>
                  <tr><td>€500 — €1,000</td><td>25%</td></tr>
                  <tr><td>€1,000 — €3,000</td><td>22%</td></tr>
                  <tr><td>Over €3,000</td><td>20%</td></tr>
                </tbody>
              </table>
            )}
            <p className="fee-note">{user?.user_type === 'professional' ? 'Prices shown excl. VAT.' : 'Prices include 20% VAT.'} Authentication fee of €30 applies when managed by The Daily Bid.</p>
          </div>
        </div>
      )}

      <nav className={`bottom-nav ${bottomNavVisible ? '' : 'hidden'}`}>
        <NavLink to="/" end className="bottom-nav-item">
          <FeedIcon />
          <span>Products</span>
        </NavLink>
        <NavLink to="/wishlist" className="bottom-nav-item">
          <WishlistIcon />
          <span>Wishlist</span>
        </NavLink>
        <NavLink to="/bid-centre" className="bottom-nav-item">
          <BidsIcon />
          <span>Bids</span>
        </NavLink>
        <NavLink to="/notifications" className="bottom-nav-item">
          <div className="bottom-nav-icon-wrap">
            <NotifIcon />
            {unreadData?.count > 0 && <span className="bottom-nav-dot">{unreadData.count}</span>}
          </div>
          <span>Alerts</span>
        </NavLink>
        <NavLink to="/profile" className="bottom-nav-item">
          <ProfileIcon />
          <span>Profile</span>
        </NavLink>
      </nav>
    </div>
  );
}
